import React, { useEffect, useState, useCallback, useRef } from 'react'
import { DataGrid, useGridApiRef, getGridStringOperators } from '@mui/x-data-grid'
import { Button, TextField } from '@mui/material'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import { format } from 'date-fns'
import axios from 'axios'
import withConfig from '../wrappers/withConfig.js'
import { constructDetailUrl } from '../../utils/constants/urls'
import { useSearchParams } from 'react-router-dom'

const NonOGWellsPage = ({ config }) => {
  const apiURL = config.API_URL
  const cancelRef = useRef(null)

  const [rows, setRows] = useState([])
  const [rowCount, setRowCount] = useState(0)

  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(50)
  const [sortModel, setSortModel] = useState([{ field: 'jobEndDate', sort: 'desc' }])
  const [serverFilterModel, setServerFilterModel] = useState({ items: [] })
  const apiRef = useGridApiRef()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [muiFilterModel, setMuiFilterModel] = useState({ items: [] });

  const [operatorFilter, setOperatorFilter] = useState('')
  const [countyFilter, setCountyFilter] = useState('')
  const [stateFilter, setStateFilter] = useState('')
  const [jobEndDateFilter, setJobEndDateFilter] = useState('')
  const [wellTypeFilter, setWellTypeFilter] = useState('')
  const [wellNameFilter, setWellNameFilter] = useState('')
  const [permitIdFilter, setPermitIdFilter] = useState('')
  const [apiNumberFilter, setApiNumberFilter] = useState('')
  const [jobStartDateFilter, setJobStartDateFilter] = useState('')
  const [searchParams, setSearchParams] = useSearchParams()

  useEffect(() => {
    setOperatorFilter(searchParams.get('operator') || '')
    setCountyFilter(searchParams.get('county') || '')
    setStateFilter(searchParams.get('state') || '')
    setJobEndDateFilter(searchParams.get('jobEndDate') || '')
    setWellTypeFilter(searchParams.get('wellType') || '')
    setWellNameFilter(searchParams.get('wellName') || '')
    setPermitIdFilter(searchParams.get('permitId') || '')
    setApiNumberFilter(searchParams.get('apiNumber') || '')
    setJobStartDateFilter(searchParams.get('jobStartDate') || '')
  }, []) 

  useEffect(() => {
    const items = []
    if (operatorFilter) items.push({ columnField: 'operatorClean', value: operatorFilter, operatorValue: 'contains' })
    if (countyFilter) items.push({ columnField: 'countyName', value: countyFilter, operatorValue: 'contains' })
    if (stateFilter) items.push({ columnField: 'stateName', value: stateFilter, operatorValue: 'contains' })
    if (jobEndDateFilter) items.push({ columnField: 'jobEndDate', value: jobEndDateFilter, operatorValue: 'equals' })
    if (wellTypeFilter) items.push({ columnField: 'wellType', value: wellTypeFilter, operatorValue: 'contains' })
    if (wellNameFilter) items.push({ columnField: 'wellName', value: wellNameFilter, operatorValue: 'contains' })
    if (permitIdFilter) items.push({ columnField: 'permitId', value: permitIdFilter, operatorValue: 'contains' })
    if (apiNumberFilter) items.push({ columnField: 'apiNumber', value: apiNumberFilter, operatorValue: 'contains' })
    if (jobStartDateFilter) items.push({ columnField: 'jobStartDate', value: jobStartDateFilter, operatorValue: 'equals' })

    const headerFieldsInUse = items.map(item => item.columnField)
    muiFilterModel.items.forEach(muiItem => {
      if (!headerFieldsInUse.includes(muiItem.field)) {
        items.push({
          columnField: muiItem.field,
          value: muiItem.value,
          operatorValue: muiItem.operator || 'contains'
        })
      }
    })

    setServerFilterModel({ items })
    setPage(0)
  }, [
    operatorFilter, countyFilter, stateFilter,
    jobEndDateFilter, wellTypeFilter, wellNameFilter,
    permitIdFilter, apiNumberFilter, jobStartDateFilter,
    muiFilterModel
  ])

  useEffect(() => {
    const params = new URLSearchParams()
    if (operatorFilter) params.set('operator', operatorFilter)
    if (countyFilter) params.set('county', countyFilter)
    if (stateFilter) params.set('state', stateFilter)
    if (jobEndDateFilter) params.set('jobEndDate', jobEndDateFilter)
    if (wellTypeFilter) params.set('wellType', wellTypeFilter)
    if (wellNameFilter) params.set('wellName', wellNameFilter)
    if (permitIdFilter) params.set('permitId', permitIdFilter)
    if (apiNumberFilter) params.set('apiNumber', apiNumberFilter)
    if (jobStartDateFilter) params.set('jobStartDate', jobStartDateFilter)
    setSearchParams(params, { replace: true })
  }, [
    operatorFilter, countyFilter, stateFilter,
    jobEndDateFilter, wellTypeFilter, wellNameFilter,
    permitIdFilter, apiNumberFilter, jobStartDateFilter,
    setSearchParams
  ])

  const allowedOperators = ['contains', 'doesNotContain', 'equals', 'doesNotEqual']
  const stringOperators = getGridStringOperators().filter((op) => allowedOperators.includes(op.value))

  const allowedDateOperators = ['equals', 'doesNotEqual']
  const dateOperators = getGridStringOperators().filter((op) =>
    allowedDateOperators.includes(op.value)
  )
  const handleMUIFilterChange = (newModel) => {
    setMuiFilterModel(newModel)
    setPage(0)
  }

  const buildQueryParams = () => {
    const sort = apiRef.current.getSortModel()[0] || {};
    const apiFilterKeys = {
      operatorClean: 'operatorName',
      countyName: 'countyName',
      stateName: 'stateName',
      jobEndDate: 'jobEndDate',
      wellType: 'wellType',
      wellName: 'wellName',
      permitId: 'permitId',
      apiNumber: 'apiNumber',
      jobStartDate: 'jobStartDate',
    }

    const apiSortField = apiFilterKeys[sort.field] || sort.field || 'jobEndDate'
    const sortDir = sort.sort || 'desc'

    // Top-level filters
    const topLevelItems = serverFilterModel.items.filter(item => item.columnField && item.value)
    const filters = Object.fromEntries(
      topLevelItems.map(item => [apiFilterKeys[item.columnField], item.value])
    )

    const params = new URLSearchParams({
      page: (page + 1).toString(),
      pageSize: pageSize.toString(),
      sortBy: apiSortField,
      sortDir,
      ...filters,
    })

    if (muiFilterModel.items.length > 0) {
      const item = muiFilterModel.items[0]
      params.append(
        'muiFilter',
        JSON.stringify({
          field: apiFilterKeys[item.field] || item.field,
          operator: item.operator || 'contains',
          value: item.value || ''
        })
      )
    } else {
      params.append('muiFilter', JSON.stringify({ field: '', operator: '', value: '' }))
    }

    return params
  }


  const fetchWells = useCallback(() => {
    if (cancelRef.current) cancelRef.current.cancel()
    setLoading(true)
    setError(null)

    const params = buildQueryParams()
    cancelRef.current = axios.CancelToken.source()

    axios
      .get(`${apiURL}/fracfocus/NonOGWells?${params.toString()}`, {
        cancelToken: cancelRef.current.token,
      })
      .then((res) => {
        const data = res.data
        const processed = (data.wells || []).map((item) => ({
          id: item.pKey,
          ...item,
        }))
        setRows(processed)
        setRowCount(data.totalCount ?? processed.length)
        setLoading(false)
      })
      .catch((err) => {
        if (!axios.isCancel(err)) {
          setError('Failed to load data')
          setLoading(false)
        }
      })
  }, [apiURL, page, pageSize, sortModel, serverFilterModel, muiFilterModel])

  useEffect(() => {
    fetchWells()
  }, [page, pageSize, sortModel, serverFilterModel, muiFilterModel, fetchWells])

  const columns = [
    { field: 'permitId', headerName: 'Permit', flex: 1, filterOperators: stringOperators },
    { field: 'apiNumber', headerName: 'API Well Number', flex: 1, filterOperators: stringOperators },
    { field: 'operatorClean', headerName: 'Operator', flex: 1, filterOperators: stringOperators },
    { field: 'wellName', headerName: 'Well Name and Number', flex: 1, filterOperators: stringOperators },
    { field: 'countyName', headerName: 'County', flex: 1, filterOperators: stringOperators },
    { field: 'stateName', headerName: 'State', flex: 1, filterOperators: stringOperators },
    { field: 'jobStartDate', headerName: 'Job Start Date', flex: 1, filterOperators: dateOperators },
    { field: 'jobEndDate', headerName: 'Job End Date', flex: 1, filterOperators: dateOperators },
    { field: 'wellType', headerName: 'Well Type', flex: 1, filterOperators: stringOperators },
    {
      field: 'disclosure',
      headerName: 'Disclosure(s)',
      width: 130,
      renderCell: (params) => (
        <Button
          variant="outlined"
          size="small"
          component="a" href={constructDetailUrl(params.row.apiNumber || params.row.pKey)}>
          View
        </Button>
      ),
      sortable: false,
      filterable: false,
    },
  ]

  const downloadCsv = async () => {
    try {
      const params = buildQueryParams()
      params.set('page', '1')
      params.set('pageSize', '100000')

      const res = await axios.get(`${apiURL}/fracfocus/NonOGWells?${params.toString()}`);
      const data = res.data?.wells || [];
      if (!data.length) {
        alert('No data to download');
        return;
      }

      const csvHeader = columns
        .filter(col => col.field !== 'disclosure')
        .map(col => col.headerName)
        .join(',');

      const escapeCsv = (value, forceString = false) => {
        if (value == null) return '';
        const str = String(value);
        if (forceString) return `="${str.replace(/"/g, '""')}"`;
        return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
      };

      const csvRows = data.map(row =>
        [
          escapeCsv(row.permitId, true),
          escapeCsv(row.apiNumber, true),
          escapeCsv(row.operatorClean),
          escapeCsv(row.wellName),
          escapeCsv(row.countyName),
          escapeCsv(row.stateName),
          escapeCsv(row.jobStartDate),
          escapeCsv(row.jobEndDate),
          escapeCsv(row.wellType),
        ].join(',')
      );

      const csvContent = [csvHeader, ...csvRows].join('\n')
      const blob = new Blob([csvContent], { type: 'text/csv' })
      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob)
      a.download = `FFNonOGList_${format(new Date(), 'yyyyMMdd')}.csv`
      a.click()
    } catch (err) {
      console.error('CSV download failed:', err)
      alert('Failed to download CSV')
    }
  };

  return (
    <div style={{ padding: 20 }}>
      <h1 style={{ textAlign: 'center', marginBottom: '1rem' }}>Disclosures Not Associated with Oil &amp; Gas Production</h1>
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 12,
            maxWidth: 900,
            width: '100%',
          }}
        >
          <TextField label="Permit" value={permitIdFilter} onChange={(e) => setPermitIdFilter(e.target.value)} size="small" />
          <TextField label="API Well Number" value={apiNumberFilter} onChange={(e) => setApiNumberFilter(e.target.value)} size="small" />
          <TextField label="Operator" value={operatorFilter} onChange={(e) => setOperatorFilter(e.target.value)} size="small" />
          <TextField label="Well Name and Number" value={wellNameFilter} onChange={(e) => setWellNameFilter(e.target.value)} size="small" />
          <TextField label="County" value={countyFilter} onChange={(e) => setCountyFilter(e.target.value)} size="small" />
          <TextField label="Well Type" value={wellTypeFilter} onChange={(e) => setWellTypeFilter(e.target.value)} size="small" />
          <div style={{ display: 'contents' }}>
            <TextField label="State" value={stateFilter} onChange={(e) => setStateFilter(e.target.value)} size="small" />
            <TextField
              label="Job Start Date"
              type="date"
              value={jobStartDateFilter}
              onChange={(e) => setJobStartDateFilter(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Job End Date"
              type="date"
              value={jobEndDateFilter}
              onChange={(e) => setJobEndDateFilter(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <div style={{gridColumn: 'span 3', textAlign: 'center', fontSize: '1.1rem', color: 'rgba(0, 0, 0, 0.6)', marginTop: -4, alignSelf: 'center' }}>
              As you enter values, a filter is applied, for dates enter exact date.
            </div>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginBottom: 16 }}>
        <Button variant="contained" className="advanced_search_button_alt" sx={{ textTransform: 'none' }} onClick={downloadCsv}>
          Download Data in Grid
        </Button>
        <Button
          variant="outlined"
          className="advanced_search_reset"
          sx={{ textTransform: 'none' }}
          onClick={() => {
            setOperatorFilter('')
            setCountyFilter('')
            setStateFilter('')
            setWellNameFilter('')
            setApiNumberFilter('')
            setPermitIdFilter('')
            setWellTypeFilter('')
            setJobStartDateFilter('')
            setJobEndDateFilter('')
            setMuiFilterModel({ items: [] })
            setPage(0)
          }}
        >
          Reset
        </Button>
      </div>

      {error && <div style={{ color: 'red' }}>{error}</div>}

      <div style={{ height: 600, width: '100%' }}>
        <DataGrid
          apiRef={apiRef}
          rows={rows}
          columns={columns}
          rowCount={rowCount}
          pagination
          paginationMode="server"
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={({ page, pageSize }) => {
            setPage(page)
            setPageSize(pageSize)
          }}
          sortingMode="server"
          filterMode="server"
          filterModel={muiFilterModel}
          onFilterModelChange={handleMUIFilterChange}
          onSortModelChange={(newSortModel) => setSortModel(newSortModel)}
          disableRowSelectionOnClick
          loading={loading}
          sx={{
            '& .MuiDataGrid-columnHeader': {
              backgroundColor: '#cce4f7 !important',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 'bold !important',
            },
          }}
        />
      </div>
    </div>
  )
}

export default withConfig(NonOGWellsPage)
